import React from "react";
import Select, {
  StylesConfig,
  GroupBase,
  MultiValue,
  SingleValue,
} from "react-select";

export interface Option {
  value: string;
  label: string;
}

type BaseProps = {
  options: Option[];
  placeholder?: string;
  isLoading?: boolean;
  isDisabled?: boolean;
  hasError?: boolean;
  className?: string;
  menuMaxHeight?: number;
};

type SingleSelectProps = BaseProps & {
  isMulti?: false;
  value?: string;
  onChange: (value: string) => void;
};

type MultiSelectProps = BaseProps & {
  isMulti: true;
  value: string[];
  onChange: (value: string[]) => void;
};

type CustomSelectProps = SingleSelectProps | MultiSelectProps;

export const CustomSelect: React.FC<CustomSelectProps> = (props) => {
  const {
    options,
    placeholder = "Selecione...",
    isLoading = false,
    isDisabled = false,
    hasError = false,
    className,
    menuMaxHeight = 200,
  } = props;

  const isMulti = "isMulti" in props && props.isMulti === true;
  const value = props.value;

  const selectedOption = React.useMemo(() => {
    if (isMulti) {
      const currentValues = (value as string[]) ?? [];
      return options.filter((opt) => currentValues.includes(opt.value));
    }
    return (
      options.find((opt) => opt.value === (value as string | undefined)) || null
    );
  }, [options, value, isMulti]);

  const handleChange = (selected: MultiValue<Option> | SingleValue<Option>) => {
    if (isMulti) {
      const multiSelected = (selected as MultiValue<Option>) || [];
      (props.onChange as (value: string[]) => void)(
        multiSelected.map((opt) => opt.value),
      );
    } else {
      const singleSelected = selected as SingleValue<Option>;
      (props.onChange as (value: string) => void)(singleSelected?.value ?? "");
    }
  };

  const customStyles: StylesConfig<Option, boolean, GroupBase<Option>> = {
    control: (base, state) => ({
      ...base,
      minHeight: "40px",
      borderColor: hasError
        ? "var(--destructive)"
        : state.isFocused
          ? "var(--ring)"
          : "var(--input)",
      borderRadius: "calc(var(--radius) - 2px)",
      backgroundColor: "var(--background)",
      boxShadow: state.isFocused ? "0 0 0 2px var(--ring)" : "none",
      "&:hover": {
        borderColor: state.isFocused ? "var(--ring)" : "var(--input)",
      },
    }),
    input: (base) => ({
      ...base,
      color: "var(--foreground)",
      fontSize: "0.875rem",
    }),
    placeholder: (base) => ({
      ...base,
      color: "var(--mutedforeground))",
      fontSize: "0.875rem",
    }),
    singleValue: (base) => ({
      ...base,
      color: "var(--foreground)",
      fontSize: "0.875rem",
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: "var(--background)",
      border: "1px solid var(--border)",
      borderRadius: "calc(var(--radius) - 2px)",
      boxShadow:
        "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
      zIndex: 50,
    }),
    menuList: (base) => ({
      ...base,
      padding: "4px",
      maxHeight: `${menuMaxHeight}px`,
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "var(--accent)"
        : state.isFocused
          ? "var(--accent)"
          : "transparent",
      color:
        state.isSelected || state.isFocused
          ? "var(--accentforeground))"
          : "var(--foreground)",
      cursor: "pointer",
      fontSize: "0.875rem",
      padding: "8px",
      borderRadius: "4px",
      "&:active": {
        backgroundColor: "var(--accent)",
      },
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: "var(--mutedforeground))",
      "&:hover": {
        color: "var(--foreground)",
      },
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    loadingIndicator: (base) => ({
      ...base,
      color: "var(--mutedforeground))",
    }),
  };

  return (
    <div className="notranslate" translate="no">
      <Select
        options={options}
        value={selectedOption}
        onChange={handleChange}
        placeholder={placeholder}
        isLoading={isLoading}
        isDisabled={isDisabled}
        styles={customStyles}
        noOptionsMessage={() => "Nenhuma opção encontrada"}
        loadingMessage={() => "Carregando..."}
        isClearable={isMulti}
        isSearchable={true}
        isMulti={isMulti}
        menuPlacement="auto"
        menuPosition="absolute"
        className={className}
        classNamePrefix="notranslate"
      />
    </div>
  );
};
